import React, { useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ToastMessage from '../../../../common/ToastMessage';
import PhotoModalComp from '../TicketModal/PhotoModalComp';
import { Button } from '@mui/material';
import { darkModeBackgroudInputs } from '../../../../utils/constants';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { TicketInterface } from '../../../../models/TicketModule/Ticket';
import { TicketImageInterface } from '../../../../models/TicketModule/TicketImage';

const allowedFormats = ['image/jpeg', 'image/png', 'image/jpg'];

interface PhotoComponentProps {
    view: boolean;
    form: TicketInterface;
    setForm: React.Dispatch<React.SetStateAction<TicketInterface>>;
}

export const PhotoComponent = ({ form, view, setForm }: PhotoComponentProps) => {
    const { darkMode } = useDarkModeStore();

    const [open, setOpen] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [photos, setPhotos] = useState<TicketImageInterface[]>([]);

    const handleOpen = (index: number) => {
        setSelectedIndex(index);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    useEffect(() => {
        let images = form?.images;
        if (images) setPhotos(images);
    }, [form.images, form.id]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {

            const maxSize = 5 * 1024 * 1024;
            // Validate file type
            if (!allowedFormats.includes(file.type)) {
                ToastMessage.Failed('Only JPG, PNG, and GIF formats are allowed.')
                return;
            }
            // Validate file size
            if (file.size > maxSize) {
                ToastMessage.Failed('File size must be less than 5MB.')
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const photoUrl = reader.result as string;
                setPhotos((prevPhotos) => {
                    const updatedPhotos = [...prevPhotos, {
                        id: Math.random().toString(),
                        ticketId: form.id || '',
                        imageUrl: photoUrl
                    }];

                    const formImages = form.formImages ? [...form.formImages, photoUrl] : [photoUrl];

                    setForm((prevForm) => ({ ...prevForm, formImages }));
                    return updatedPhotos;
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCameraClick = () => {
        const fileInput = document.getElementById('photoInput') as HTMLInputElement;
        fileInput.click();
    };

    const handleDeletePhoto = (id: string, photoUrl: string) => {
        setPhotos((prevPhotos) => {
            const updatedPhotos = prevPhotos.filter(photo => photo.imageUrl !== photoUrl);
            const images = form.images?.filter(item => item.id !== id);
            const formImages = form.formImages?.filter(item => item !== photoUrl);
            setForm((prevForm) => ({ ...prevForm, images, formImages }));
            return updatedPhotos;
        });
    };

    return (
        <div className='d-flex mt-4 align-items-center gap-2 pt-2 pb-2' style={{ overflowX: 'auto' }}>
            {photos.map((photo, index) => (
                <div key={index} style={{ position: 'relative' }}>
                    <Button
                        variant="contained"
                        onClick={() => handleOpen(index)}
                        sx={{
                            height: '60px',
                            width: '90px',
                            background: '#F3F6F9',
                            color: '#413420',
                            '&:hover': {
                                backgroundColor: '#E1E5E8',
                                transform: 'scale(1.1)',
                            },
                            padding: 0,
                            transition: 'transform 0.3s ease',
                        }}
                    >
                        <img
                            src={photo.imageUrl}
                            alt="Uploaded"
                            style={{
                                height: '100%',
                                width: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px',
                            }}
                        />
                    </Button>
                    {
                        !view
                        && <IconButton
                            onClick={() => handleDeletePhoto(photo.id, photo.imageUrl)}
                            sx={{
                                top: '1px',
                                right: '1px',
                                width: '10px',
                                height: '10px',
                                fontSize: '8px',
                                color: '#FF0000',
                                position: 'absolute',
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    backgroundColor: '#FFDDDD',
                                },
                            }}
                        >
                            ✕
                        </IconButton>
                    }
                </div>
            ))}

            <input
                type="file"
                id="photoInput"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept={allowedFormats.join(',')}
            />

            {!view
                && <Button
                    variant="contained"
                    sx={{
                        width: '90px',
                        height: '60px',
                        color: darkMode ? 'white' : '#413420',
                        background: darkMode ? darkModeBackgroudInputs : '#F3F6F9',
                        '&:hover': {
                            backgroundColor: '#E1E5E8',
                        },
                    }}
                    onClick={handleCameraClick}
                >
                    <CameraAltIcon />
                </Button>}
            <PhotoModalComp
                open={open}
                photos={photos}
                selectedIndex={selectedIndex}
                handleClose={handleClose}
            />
        </div>
    );
};

export default PhotoComponent;
